import {
  APP_ID_ADS,
  APP_ID_SINGLE_ZONE_SCHEDULER,
  SYSTEM_MANAGED_APPS,
} from "constant/application";
import get from "lodash/get";
import { defaultArray, defaultObject } from "redux-store/selectors";
import { createSelector } from "reselect";
import { features } from "ui/features";

function _applicationsSelector(applications) {
  return applications.byId || defaultObject;
}

function _getNonSchedulerApps(byId) {
  return {
    byId: Object.keys(byId).reduce((acc, id) => {
      const app = byId[id];
      if (app.name !== APP_ID_SINGLE_ZONE_SCHEDULER) {
        acc[id] = app;
      }
      return acc;
    }, {}),
  };
}

const getAllByIdExceptSzs = createSelector(
  _applicationsSelector,
  _getNonSchedulerApps,
);

function _byIdSelector(byId) {
  return byId;
}

function _byAppName(byId, appName) {
  return Object.values(byId).find((a) => a.name === appName);
}

function _applicationNameSelector(byId, appName) {
  return appName;
}

const getByApplicationName = createSelector(
  _byIdSelector,
  _applicationNameSelector,
  _byAppName,
);

function applicationsSelector(state) {
  return get(state, "global.applications.byId", defaultObject);
}

function compatApplicationsSelector(state) {
  // TODO(jhohman): Use this reducer to get all applications until
  //   the views have been refactored to handle available and unavailable apps.
  //   The allApps property will be removed once complete.
  return get(state, "global.applications.allApps", defaultObject);
}

function available(app) {
  return app.available;
}

function icon(app) {
  const { icon128, icon256, icon512 } = app;

  if (icon512) {
    return icon512;
  }

  if (icon256) {
    return icon256;
  }

  return icon128;
}

function name(app) {
  const displayName = app.display_name;

  if (displayName) {
    return displayName;
  }

  return app.name;
}

function searchAllApps(applications, id) {
  return get(applications, `allApps.${id}`, null);
}

function systemManaged(app) {
  return SYSTEM_MANAGED_APPS.includes(app.name);
}

function installed(app) {
  return app && (app.is_own || app.is_installed);
}

function isVistarAdsApp(app) {
  return app.name === APP_ID_ADS;
}

const installedAppsSelector = createSelector(
  (apps) => apps || defaultArray,
  _getInstalledApps,
);

function _getInstalledApps(apps) {
  return apps.filter((app) => app.is_installed && !app.hide_on_ui);
}

function getPublicApps(apps) {
  return apps.filter((app) => !app.is_own && app.type === "APPLICATION");
}

function getScreenshots(app, type) {
  const appType = app[type];
  const screenshots = [];
  for (let i = 1; i <= 5; i++) {
    const screenshot = appType[`screenshot${i}`];
    if (screenshot) {
      screenshots.push(screenshot);
    }
  }
  return screenshots;
}

function getUserParams(app, type) {
  return app[type].user_params;
}

function getAppParamOptions(apps) {
  return apps.reduce((acc, app) => {
    const version = app.stable_version || app.testing_version || {};
    const userParams = version.user_params;
    const { name: appName, display_name: displayName } = app;

    if (userParams) {
      userParams.forEach((param) => {
        const tag = {
          label: displayName + ": " + param.name,
          value: features.APP_PARAMETERS_V3
            ? `${param.name}:${appName}`
            : param.name,
        };
        acc.push(tag);
      });
    }

    return acc;
  }, []);
}

export {
  _getInstalledApps,
  applicationsSelector,
  available,
  compatApplicationsSelector,
  getAllByIdExceptSzs,
  getAppParamOptions,
  getByApplicationName,
  getPublicApps,
  getScreenshots,
  getUserParams,
  icon,
  installed,
  installedAppsSelector,
  isVistarAdsApp,
  name,
  searchAllApps,
  systemManaged,
};
